/**
 * 语言解析与 storage（不依赖 vue-i18n 实例，避免与 locale/index 循环引用）。
 *
 * @module locale/locale-state
 */

import { Locale } from 'wot-design-uni'
import wotEnUS from 'wot-design-uni/locale/lang/en-US'

// --- 类型定义 ---

/** 与 0息游外跳 `language` query 及 uni locale 对齐 */
export type AppLocale = 'zh-Hans' | 'en'

export type LocalePageOptions = Record<string, string | undefined> | undefined

// --- 常量与变量 ---

export const APP_LOCALE_STORAGE_KEY = 'appLocale'

export const SUPPORTED_LOCALES: AppLocale[] = ['zh-Hans', 'en']

// --- 函数 ---

function normalizeOptionValue(raw: string | undefined): string {
  if (raw == null || raw === '')
    return ''
  try {
    return decodeURIComponent(String(raw)).trim()
  }
  catch {
    return String(raw).trim()
  }
}

/**
 * 将 URL / storage 中的语言值规范为 `zh-Hans` 或 `en`。
 */
export function normalizeAppLocale(raw: string | undefined): AppLocale | null {
  const normalized = normalizeOptionValue(raw)
  if (!normalized)
    return null
  if (normalized === 'en' || normalized.toLowerCase().startsWith('en-'))
    return 'en'
  if (normalized === 'zh-Hans' || normalized === 'zh-CN' || normalized.toLowerCase().startsWith('zh'))
    return 'zh-Hans'
  return null
}

/** 读取本地持久化的语言（无效则 null） */
export function readStoredAppLocale(): AppLocale | null {
  try {
    const stored = uni.getStorageSync(APP_LOCALE_STORAGE_KEY)
    return normalizeAppLocale(typeof stored === 'string' ? stored : '')
  }
  catch {
    return null
  }
}

/**
 * 启动或进页：优先 URL `language`，其次 storage，再次 uni 当前 locale，默认 zh-Hans。
 */
export function resolveAppLocale(options?: LocalePageOptions): AppLocale {
  const fromOptions = normalizeAppLocale(options?.language)
  if (fromOptions)
    return fromOptions

  const stored = readStoredAppLocale()
  if (stored)
    return stored

  const current = normalizeAppLocale(uni.getLocale())
  if (current)
    return current

  return 'zh-Hans'
}

type WotDesignLocale = 'zh-CN' | 'en-US'

/** 后端 Accept-Language 取值（与 AppLocale 对齐） */
export type AcceptLanguageHeader = 'en' | 'zh-CN'

function resolveWotDesignLocale(appLocale: AppLocale): WotDesignLocale {
  return appLocale === 'en' ? 'en-US' : 'zh-CN'
}

export function resolveAcceptLanguageHeader(appLocale: AppLocale): AcceptLanguageHeader {
  return appLocale === 'en' ? 'en' : 'zh-CN'
}

/** 将 Wot Design Uni 组件语言与当前 AppLocale 对齐 */
export function syncWotDesignLocale(appLocale: AppLocale): void {
  const wotLocale = resolveWotDesignLocale(appLocale)
  if (wotLocale === 'en-US') {
    Locale.use('en-US', wotEnUS)
    return
  }
  Locale.use('zh-CN')
}
